import type { ReactNode } from 'react';
import Button from './Button';

/**
 * C10 CTA panels on the brand gradient.
 *  - inline: text left, white button right, offset toward the right edge of
 *    the wide container (Premier's asymmetry); centred/stacked on phones.
 *  - large: centred H2 (light), paragraph, white button, optional proof line.
 *
 *   <CtaPanel text="Ready for a gentle first visit?" button={{ label: 'Book Online', to: SITE.booking.chooserPath }} />
 *   <CtaPanel variant="large" title="Start with a conversation" text="..." button={{ label: 'Contact us', to: '/contact-us/' }} />
 */
export interface CtaButton {
  label: string;
  href?: string;
  to?: string;
  external?: boolean;
}

export interface CtaPanelProps {
  variant?: 'inline' | 'large';
  title?: ReactNode;
  text: ReactNode;
  button: CtaButton;
  /** Extra line under the button (e.g. <StarRating /> + a label). */
  proof?: ReactNode;
  /** Wrap in a `.ve-container--wide` + vertical padding (default true). */
  contained?: boolean;
  headingId?: string;
  className?: string;
}

export default function CtaPanel({
  variant = 'inline',
  title,
  text,
  button,
  proof,
  contained = true,
  headingId,
  className = '',
}: CtaPanelProps) {
  const panel = (
    <div className={`ve-cta ve-cta--${variant} ${className}`.trim()}>
      {variant === 'large' ? (
        <>
          {title && <h2 id={headingId}>{title}</h2>}
          <p className="ve-cta-text">{text}</p>
          <Button variant="white" size="lg" href={button.href} to={button.to} external={button.external}>
            {button.label}
          </Button>
          {proof && <div className="ve-cta-proof">{proof}</div>}
        </>
      ) : (
        <>
          <p className="ve-cta-text">
            {title && (
              <>
                <strong>{title}</strong>{' '}
              </>
            )}
            {text}
          </p>
          <Button variant="white" href={button.href} to={button.to} external={button.external}>
            {button.label}
          </Button>
        </>
      )}
    </div>
  );
  if (!contained) return panel;
  return (
    <div className="ve-cta-wrap">
      <div className="ve-container ve-container--wide">{panel}</div>
    </div>
  );
}
